import React, { forwardRef } from 'react';
import Card from './card';

const borderAccentVariants = {
    default: {},
    accent: { borderTop: '3px solid var(--accent)' },
    success: { borderTop: '3px solid var(--success)' },
    warning: { borderTop: '3px solid var(--warning)' },
    danger: { borderTop: '3px solid var(--danger)' },
    info: { borderTop: '3px solid var(--info)' }
};

const iconBgVariants = {
    default: { backgroundColor: 'var(--accent-soft)', color: 'var(--accent-ink)' },
    accent: { backgroundColor: 'var(--accent-soft)', color: 'var(--accent-ink)' },
    success: { backgroundColor: 'var(--success-soft)', color: 'var(--success)' },
    warning: { backgroundColor: 'var(--warning-soft)', color: 'var(--warning)' },
    danger: { backgroundColor: 'var(--danger-soft)', color: 'var(--danger)' },
    info: { backgroundColor: 'var(--info-soft)', color: 'var(--info)' }
};

const MetricCard = forwardRef(({
    label,
    title,
    value,
    subtext,
    subtitle,
    icon,
    trend,
    badge,
    variant = 'default',
    onClick,
    loading = false,
    className,
    style,
    ...props
}, ref) => {
    const metricLabel = label || title;
    const metricSubtext = subtext || subtitle;
    const accentStyle = borderAccentVariants[variant] || borderAccentVariants.default;
    const iconStyle = iconBgVariants[variant] || iconBgVariants.default;

    const renderTrend = () => {
        if (!trend) return null;
        if (typeof trend === 'string' || React.isValidElement(trend)) {
            return (
                <span
                    style={{
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: 'var(--accent-ink)',
                        backgroundColor: 'var(--accent-soft)',
                        padding: '0.125rem 0.5rem',
                        borderRadius: 'var(--radius-pill)'
                    }}
                >
                    {trend}
                </span>
            );
        }
        const isPos = trend.positive !== false;
        return (
            <span
                style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: isPos ? 'var(--success)' : 'var(--danger)',
                    backgroundColor: isPos ? 'var(--success-soft)' : 'var(--danger-soft)',
                    padding: '0.125rem 0.5rem',
                    borderRadius: 'var(--radius-pill)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                }}
            >
                {trend.value}
            </span>
        );
    };

    return (
        <Card
            ref={ref}
            className={className}
            onClick={onClick}
            style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: '1.25rem',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                ...accentStyle,
                ...style
            }}
            {...props}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span
                        style={{
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: 'var(--text-muted)',
                            fontFamily: 'var(--font-ui)'
                        }}
                    >
                        {metricLabel}
                    </span>
                    {loading ? (
                        <div
                            style={{
                                width: '6rem',
                                height: '2rem',
                                backgroundColor: 'var(--surface-sunken)',
                                borderRadius: 'var(--radius-sm)',
                                marginTop: '0.25rem',
                                animation: 'pulse 1.5s infinite'
                            }}
                        />
                    ) : (
                        <div
                            style={{
                                fontSize: '1.875rem',
                                fontWeight: '700',
                                color: 'var(--text)',
                                fontFamily: 'var(--font-display)',
                                lineHeight: '1.2',
                                marginTop: '0.125rem'
                            }}
                        >
                            {value}
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {badge}
                    {icon && (
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0.5rem',
                                borderRadius: 'var(--radius-md)',
                                ...iconStyle
                            }}
                        >
                            {icon}
                        </div>
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.75rem', gap: '0.5rem' }}>
                {metricSubtext ? (
                    <span
                        style={{
                            fontSize: '0.75rem',
                            color: 'var(--text-muted)',
                            fontFamily: 'var(--font-ui)'
                        }}
                    >
                        {metricSubtext}
                    </span>
                ) : <span />}
                {renderTrend()}
            </div>
        </Card>
    );
});

MetricCard.displayName = 'MetricCard';

export { MetricCard };
export default MetricCard;
